import React from 'react';
import clsx from 'clsx';
import {
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import type { ToolResultBlock, ToolUseBlock } from '../../../store/chatStore';
import { safeJsonParse } from '../../../../../utils/jsonRepairUtils';
import { Image } from '../../../../ui/Image';
import {
  PleriActionCell,
  type MoreOption,
} from './Table/components/PleriActionCell';
import type { GenericItem } from './Table/types';
import {
  isRecord,
  getString,
  getFirstString,
  splitByAssetName,
} from './shared/fieldUtils';
import { getResourceImageFallback } from './shared/imageFallback';
import type { TFunction } from 'i18next';

import { useTranslation } from 'react-i18next';

interface Props {
  result: ToolResultBlock;
  toolUse: ToolUseBlock;
}

type ChipTone = 'neutral' | 'info' | 'warning' | 'good' | 'bad';

interface ChipProps {
  label: string;
  tone?: ChipTone;
  size?: 'default' | 'compact';
}

const toneClasses: Record<ChipTone, string> = {
  neutral: 'bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100',
  info: 'bg-sky-100 dark:bg-sky-900 text-sky-900 dark:text-sky-200',
  warning: 'bg-amber-100 dark:bg-amber-900 text-amber-900 dark:text-amber-200',
  good: 'bg-emerald-100 dark:bg-emerald-900 text-emerald-900 dark:text-emerald-200',
  bad: 'bg-rose-100 dark:bg-rose-900 text-rose-900 dark:text-rose-200',
};

const vulnerabilityMoreOptions = (t: TFunction<'pleri/chat'>): MoreOption[] => [
  {
    label: t('suggestFix'),
    message: 'Suggest fix for this vulnerability in the given asset.',
  },
  {
    label: t('createTicket'),
    message: 'Can you help me create a ticket for this?',
  },
  {
    label: t('exempt'),
    message: 'Exempt this vulnerability.',
  },
];

const Chip: React.FC<ChipProps> = ({
  label,
  tone = 'neutral',
  size = 'default',
}) => {
  return (
    <span
      className={clsx(
        'inline-flex max-w-full items-center rounded-md font-normal',
        size === 'compact' ? 'px-2 py-0.5 text-xs' : 'px-2 py-1 text-xs',
        toneClasses[tone],
      )}
    >
      <span className="truncate">{label}</span>
    </span>
  );
};

function getBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes'].includes(normalized)) {
      return true;
    }
    if (['false', '0', 'no'].includes(normalized)) {
      return false;
    }
  }
  return undefined;
}

function parseVulnerabilities(content: unknown): Record<string, unknown>[] {
  const parsed =
    typeof content === 'string'
      ? safeJsonParse<unknown>(content, 'listVulnerabilities result')
      : content;

  if (Array.isArray(parsed)) {
    return parsed.filter(isRecord);
  }

  if (!isRecord(parsed) || !Array.isArray(parsed.data)) {
    return [];
  }

  return parsed.data.filter(isRecord);
}

function toTitleCase(value: string): string {
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getSeverityChip(value: unknown): {
  label: string;
  classes: string;
} | null {
  const severity = getString(value)?.trim();
  if (!severity) {
    return null;
  }

  const normalized = severity.toLowerCase();
  if (normalized === 'critical' || normalized === 'high') {
    if (normalized === 'critical') {
      return {
        label: toTitleCase(severity),
        classes: 'bg-severity-critical-background text-severity-critical-base',
      };
    }
    return {
      label: toTitleCase(severity),
      classes: 'bg-severity-high-background text-severity-high-base',
    };
  }
  if (normalized === 'medium') {
    return {
      label: toTitleCase(severity),
      classes: 'bg-severity-medium-background text-severity-medium-base',
    };
  }
  if (normalized === 'low') {
    return {
      label: toTitleCase(severity),
      classes: 'bg-severity-low-background text-severity-low-base',
    };
  }
  if (normalized === 'info' || normalized === 'informational') {
    return {
      label: toTitleCase(severity),
      classes:
        'bg-severity-informational-background text-severity-informational-base',
    };
  }

  return {
    label: toTitleCase(severity),
    classes: 'bg-severity-unknown-background text-severity-unknown-base',
  };
}

function normalizeSummaryText(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

export const VulnerabilitiesTableRenderer: React.FC<Props> = ({ result }) => {
  const { t } = useTranslation('pleri/chat');
  if (result.isError) {
    return (
      <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950">
        {typeof result.content === 'string'
          ? result.content
          : JSON.stringify(result.content, null, 2)}
      </div>
    );
  }

  const vulnerabilities = parseVulnerabilities(result.content);
  if (vulnerabilities.length === 0) {
    return (
      <div className="py-2 text-sm text-slate-600 dark:text-slate-400">
        {t('noVulnerabilitiesFound')}
      </div>
    );
  }

  return (
    <div className="space-y-4 font-sans whitespace-normal">
      {vulnerabilities.map((vulnerability, index) => {
        const vulnerabilityId =
          getString(vulnerability.vulnerabilityId) ?? `vulnerability-${index}`;
        const summarySource =
          getString(vulnerability.summary) ??
          getString(vulnerability.rewrittenSummary) ??
          getString(vulnerability.displaySummary) ??
          getString(vulnerability.title) ??
          getString(vulnerability.description) ??
          getString(vulnerability.vulnerabilityId) ??
          t('untitledVulnerability');
        const summary = normalizeSummaryText(summarySource);
        const assetName = getFirstString(
          vulnerability.assetName,
          vulnerability.resourceName,
          vulnerability.asset_name,
          vulnerability.resource_name,
        );
        const summaryParts = splitByAssetName(summary, assetName);
        const summaryContent = summaryParts ? (
          <>
            {summaryParts.before}
            <strong className="font-semibold">{summaryParts.match}</strong>
            {summaryParts.after}
          </>
        ) : (
          summary
        );
        const primaryUrl = getString(vulnerability.primaryUrl);
        const severity = getSeverityChip(vulnerability.severityLevel);
        const hasVendorFix = getBoolean(vulnerability.hasVendorFix);
        const resourceType =
          getFirstString(
            vulnerability.assetType,
            vulnerability.resourceType,
            vulnerability.resource_type,
          ) ?? 'resource';
        const provider = getFirstString(
          vulnerability.provider,
          vulnerability.provider_name,
          vulnerability.assetProvider,
          vulnerability.asset_provider,
          vulnerability.cloudProvider,
          vulnerability.cloud_provider,
        );
        const assetIdentifier = getString(vulnerability.assetId);
        const rawVulnerabilityId = getString(vulnerability.vulnerabilityId);
        const vulnerabilityDetailUrl =
          rawVulnerabilityId && assetIdentifier
            ? `/vulnerabilities/view/${btoa(rawVulnerabilityId)}/${btoa(assetIdentifier)}`
            : undefined;

        return (
          <article
            key={vulnerabilityId}
            className="group/asset-card relative min-h-[100px] overflow-visible rounded-xl border border-slate-200 dark:border-slate-700 bg-white p-4 pr-32 shadow-sm transition-shadow hover:shadow-md dark:bg-gray-900"
          >
            <PleriActionCell
              row={{
                original: {
                  ...(vulnerability as GenericItem),
                  vulnerabilityId,
                  assetId: assetIdentifier ?? '',
                },
              }}
              primaryAction={
                assetIdentifier
                  ? {
                      label: t('learnMoreAboutThisAsset'),
                      // Sent to the agent, so it stays English.
                      message: `Learn more about the asset associated with this vulnerability. Asset ID: ${assetIdentifier}`,
                    }
                  : undefined
              }
              showRemediationButton
              remediationConfig={{
                tooltip: t('analyzeExploitability'),
                message: `Analyze the exploitability of vulnerability ${vulnerabilityId} on this asset`,
                icon: MagnifyingGlassIcon,
                hoverClasses:
                  'hover:bg-orange-100 hover:text-orange-700 hover:ring-orange-300',
              }}
              moreOptions={vulnerabilityMoreOptions(t)}
              position="right"
              hoverScope="asset-card"
              alwaysVisible
              size="compact"
              direction="vertical"
            />
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 flex-shrink-0 overflow-hidden rounded-md bg-slate-100 dark:bg-slate-700">
                <Image
                  type="resourceType"
                  name={resourceType}
                  fallback={getResourceImageFallback({
                    provider,
                    resourceType,
                  })}
                  className="h-full w-full object-contain p-0.5"
                />
              </div>
              {primaryUrl ? (
                <a
                  href={primaryUrl}
                  rel="noreferrer noopener"
                  target="_blank"
                  className={clsx(
                    'inline-flex max-w-full items-center rounded-md px-2 py-0.5 text-xs font-normal',
                    toneClasses.info,
                    'hover:opacity-90',
                  )}
                  title={t('openInANewTab', { vulnerabilityId })}
                >
                  <span className="truncate">{vulnerabilityId}</span>
                  <ArrowTopRightOnSquareIcon className="ml-1 h-2.5 w-2.5 flex-shrink-0" />
                </a>
              ) : (
                <Chip label={vulnerabilityId} tone="info" size="compact" />
              )}
              {severity && (
                <span
                  className={clsx(
                    'inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-xs font-semibold',
                    severity.classes,
                  )}
                  title={t('severity')}
                >
                  {severity.label}
                </span>
              )}
            </div>
            <div
              className="mt-2 min-w-0 text-sm break-words whitespace-normal text-slate-800 dark:text-slate-200"
              title={t('vulnerabilitySummary', { summary })}
            >
              {vulnerabilityDetailUrl ? (
                <a
                  href={vulnerabilityDetailUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-slate-800 hover:text-slate-900 dark:text-slate-200 dark:hover:text-slate-100"
                >
                  {summaryContent}
                  <span className="ml-1 inline-flex h-3.5 w-3.5 translate-y-[1px] items-center justify-center rounded bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-300">
                    <ArrowTopRightOnSquareIcon className="h-2 w-2" />
                  </span>
                </a>
              ) : (
                summaryContent
              )}
            </div>
            {(hasVendorFix === true || hasVendorFix === false) && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {hasVendorFix === true && (
                  <Chip label={t('fixable')} tone="good" size="compact" />
                )}
                {hasVendorFix === false && (
                  <Chip label={t('notFixable')} tone="bad" size="compact" />
                )}
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
};
